import React, {
  useState,
  useEffect,
  createContext,
  useCallback,
  useContext,
  ReactNode,
} from 'react'

import { isEqual } from 'lodash'

import { AnalyticsManager } from '@/utils/analytics/analytics'
import {
  AnalyticsIdentifyProperties,
  AnalyticsPageProperties,
  AnalyticsTrackProperties,
} from '@/utils/analytics/analyticsTypes'

interface AnalyticsInterface {
  page: (pageName: string, properties?: AnalyticsPageProperties) => void
  identify: (identifyProperties: AnalyticsIdentifyProperties) => void
  track: (eventName: string, properties?: AnalyticsTrackProperties) => void
}

export interface AnalyticsContextProps {
  analytics: AnalyticsInterface | undefined
}

export const AnalyticsContext = createContext<AnalyticsManager | undefined>(
  undefined
)

const useAnalyticsContext = () => useContext(AnalyticsContext)

interface AnalyticsProviderProps {
  children: ReactNode
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [analyticsManager, setAnalyticsManager] = useState<
    AnalyticsManager | undefined
  >(undefined)

  const loadAnalytics = async () => {
    if (analyticsManager) {
      return
    }

    const analyticsManagerInstance = new AnalyticsManager()
    await analyticsManagerInstance.load()
    setAnalyticsManager(analyticsManagerInstance)
  }

  useEffect(() => {
    loadAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AnalyticsContext.Provider value={analyticsManager}>
      {children}
    </AnalyticsContext.Provider>
  )
}

const identifyUserBacklog = []
const trackEventBacklog = []
const pageViewBacklog = []

let lastIdentifyProperties: AnalyticsIdentifyProperties | null = null

const useAnalytics = () => {
  const analyticsManager = useAnalyticsContext()

  useEffect(() => {
    if (!analyticsManager) {
      return
    }

    while (pageViewBacklog.length) {
      const { pageName, properties } = pageViewBacklog.shift()
      analyticsManager?.pageViewed(pageName, properties)
    }

    while (identifyUserBacklog.length) {
      const identifyProperties = identifyUserBacklog.shift()

      // prevent exessive (and expensive $$$) calls to identifyUser on page re-renders
      if (!isEqual(identifyProperties, lastIdentifyProperties)) {
        analyticsManager.identifyUser(identifyProperties)
        lastIdentifyProperties = identifyProperties
      }
    }

    while (trackEventBacklog.length) {
      const { eventName, properties } = trackEventBacklog.shift()
      analyticsManager?.trackEvent(eventName, properties)
    }
  }, [analyticsManager])

  const pageViewed = useCallback(
    (pageName: string, properties?: AnalyticsPageProperties) => {
      if (!analyticsManager) {
        pageViewBacklog.push({ pageName, properties })
      } else {
        analyticsManager?.pageViewed(pageName, properties)
      }
    },
    [analyticsManager]
  )

  const identifyUser = useCallback(
    (identifyProperties: AnalyticsIdentifyProperties) => {
      if (!analyticsManager) {
        identifyUserBacklog.push(identifyProperties)
        return
      }

      // prevent exessive (and expensive $$$) calls to identifyUser on page re-renders
      if (!isEqual(identifyProperties, lastIdentifyProperties)) {
        analyticsManager.identifyUser(identifyProperties)
        lastIdentifyProperties = identifyProperties
      }
    },
    [analyticsManager]
  )

  const trackEvent = useCallback(
    (eventName: string, properties?: AnalyticsTrackProperties) => {
      if (!analyticsManager) {
        trackEventBacklog.push({ eventName, properties })
      } else {
        analyticsManager?.trackEvent(eventName, properties)
      }
    },
    [analyticsManager]
  )

  return {
    pageViewed,
    identifyUser,
    trackEvent,
  }
}

export default useAnalytics
